/**
 * Effective Permissions Store — persists the user's resolved effective
 * permissions per module in TotalAccessOfflineDB (`app-shell` domain).
 *
 * This is the offline source of truth for action permissions (create,
 * resolve, close, smart_task, etc.) and feature settings that control
 * what a user can do inside workflow modals. The data is fetched from
 * `/api/v1/iam/effective-permissions` and synced via masterSync.ts.
 *
 * Storage shape: a single KV record per client+module containing the
 * full effective-permissions API response.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

const DOMAIN = 'app-shell' as const;
const STORE = 'effective-permissions' as const;

export interface StoredEffectivePermissions {
  module_key: string;
  permissions: Record<string, unknown>;
  role: string;
  has_access: boolean;
  resolved_via: Record<string, string>;
  overrides_only: Record<string, unknown>;
  is_field_worker: boolean;
  is_root: boolean;
  /** Per-solution feature rows (source_solution_key → key → value) — optional for older caches. */
  solution_permissions?: Record<string, Record<string, unknown>>;
  synced_at: string;
}

function getDb() {
  return offlineDb;
}

function entityKey(moduleKey: string): string {
  return `effective-permissions:${moduleKey}`;
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function getStoreRow(clientId: number, moduleKey: string): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }
  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, STORE, clientId, entityKey(moduleKey)])
    .first();
}

const effectivePermissionsStore = {
  /** Persist effective permissions for the current client + module. */
  async save(
    clientId: number,
    moduleKey: string,
    data: Omit<StoredEffectivePermissions, 'synced_at'>,
  ): Promise<void> {
    const db = getDb();
    if (!db || !clientId || !moduleKey) {
      return;
    }

    const existing = await getStoreRow(clientId, moduleKey);
    const now = new Date().toISOString();
    const payload: StoredEffectivePermissions = {
      ...data,
      synced_at: now,
    };
    const encrypted = await offlinePayloadCrypto.encryptValue(payload);

    const row: OfflineDomainRecord = {
      id: existing?.id,
      domain: DOMAIN,
      store: STORE,
      client_id: clientId,
      entity_id: entityKey(moduleKey),
      sync_status: 'synced',
      created_at: existing?.created_at || now,
      updated_at: now,
      payload: encrypted,
      payload_size: encrypted.length,
    };

    await db.records.put(row);
  },

  /** Read cached effective permissions from the offline DB. */
  async load(clientId: number, moduleKey: string): Promise<StoredEffectivePermissions | null> {
    const row = await getStoreRow(clientId, moduleKey);
    const decrypted = await decryptRecordPayload<StoredEffectivePermissions>(row);
    if (decrypted === undefined || decrypted === null) {
      return null;
    }
    return decrypted as StoredEffectivePermissions;
  },

  /** Clear cached permissions for a module (e.g. on logout). */
  async clear(clientId: number, moduleKey: string): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow(clientId, moduleKey);
    if (row?.id !== undefined && typeof row.id === 'number') {
      await db.records.delete(row.id);
    }
  },

  /** Clear all cached permissions for a client (e.g. on logout). */
  async clearAll(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const rows = await db.records
      .where('[domain+store+client_id]')
      .equals([DOMAIN, STORE, clientId])
      .toArray();
    const ids = rows.map((r) => r.id).filter((id): id is number => typeof id === 'number');
    if (ids.length > 0) {
      await db.records.bulkDelete(ids);
    }
  },
};

export default effectivePermissionsStore;
